/**
 * まとめて精算(2026-09-04 発注者指示 → 2026-09-07 会社ごとに分割)。
 * チェックした立替(同じ立替者)をまとめて精算する。1件だけでも同じ画面を使う。
 *  - 申請の会社ごとに枠を分け、それぞれ精算日と精算に使う口座(その会社の口座・現金だけ)を選ぶ
 *  - 現金: その場で立替者に手書きサインをもらう(受領書の代わり)。サインは1回で、現金で渡す会社分すべての一覧PDFに入る
 *  - 振込: 会社ごとに振込明細を添付(文言なしでOK)
 *  - 会社ごとに一覧PDFと精算記録を作り、各申請の詳細・完了確認から開ける
 */
import { useEffect, useMemo, useState } from 'react';
import { formatCashAccountLabel } from '@core/lib/cashAccountLabel';
import Modal from '@core/components/Modal';
import DateField from '@core/components/DateField';
import FileDropZone from '@core/expense/FileDropZone';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import { uploadSettlementFiles, validateFile } from '@core/expense/attachments';
import { toShortDate, todayStr } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { type CashAccount, type Transaction } from '@core/lib/types';
import { loadImage } from './signedReceipt';
import { buildSettlementListPdf } from './settlementPdf';
import { newSettlementId, reimburseBatch } from './flowActions';
import { reportFlowResult } from './flowResult';
import SignaturePadModal from './SignaturePadModal';

/** 会社ごとの入力(精算日・口座・振込明細) */
type GroupInput = { date: string; cashAccountId: string; files: File[] };
const emptyInput = (): GroupInput => ({ date: todayStr(), cashAccountId: '', files: [] });

export default function BatchSettleModal({
  transactions,
  payeeId,
  payeeName,
  onClose,
  onDone,
}: {
  transactions: Transaction[];
  payeeId: string;
  payeeName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { user } = useAuth();
  const { accountName, companyName, companies, defaultCompanyId } = useMasters();
  const toast = useToast();
  const [accounts, setAccounts] = useState<CashAccount[]>([]);
  const [inputs, setInputs] = useState<Record<string, GroupInput>>({});
  const [signature, setSignature] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [busy, setBusy] = useState(false);

  const total = useMemo(() => transactions.reduce((s, t) => s + t.amount, 0), [transactions]);

  /** 会社ごとにまとめる(会社マスタの並び順) */
  const groups = useMemo(() => {
    const order = new Map(companies.map((c, i) => [c.id, i]));
    const map = new Map<string, Transaction[]>();
    for (const t of transactions) {
      const key = t.company_id ?? defaultCompanyId;
      map.set(key, [...(map.get(key) ?? []), t]);
    }
    return [...map.entries()]
      .sort((a, b) => (order.get(a[0]) ?? 99) - (order.get(b[0]) ?? 99))
      .map(([companyId, items]) => ({ companyId, items, total: items.reduce((s, t) => s + t.amount, 0) }));
  }, [transactions, companies, defaultCompanyId]);

  useEffect(() => {
    // クレカでは精算(現金の受け渡し・振込)できないため除外
    fetchCashAccounts()
      .then((list) => setAccounts(list.filter((a) => a.active && a.kind !== 'card')))
      .catch(() => setAccounts([]));
  }, []);

  const inputOf = (companyId: string): GroupInput => inputs[companyId] ?? emptyInput();
  const patch = (companyId: string, p: Partial<GroupInput>) =>
    setInputs((prev) => ({ ...prev, [companyId]: { ...(prev[companyId] ?? emptyInput()), ...p } }));

  /** その会社の口座・現金だけを出す。未登録なら全社の口座(会社名つき)で代用 */
  const accountsFor = (companyId: string): { list: CashAccount[]; fallback: boolean } => {
    const mine = companyId === 'sonota' ? accounts : accounts.filter((a) => a.company_id === companyId);
    return mine.length > 0 ? { list: mine, fallback: false } : { list: accounts, fallback: true };
  };
  const accountOf = (companyId: string) => accounts.find((a) => a.id === inputOf(companyId).cashAccountId) ?? null;
  // 受け渡しの方法は口座の種別で決まる(2026-09-07 指示: 現金→その場でサイン / 銀行→振込明細)
  const methodOf = (companyId: string): 'cash' | 'bank_transfer' =>
    accountOf(companyId)?.kind === 'cash' ? 'cash' : 'bank_transfer';
  const needsSignature = groups.some((g) => accountOf(g.companyId)?.kind === 'cash');
  // 表記は「会社名: 口座名」(2026-09-08 指示)
  const accountLabel = (a: CashAccount) => formatCashAccountLabel(a, companyName(a.company_id));

  async function submit() {
    if (!user) return;
    if (transactions.length === 0) return;
    for (const g of groups) {
      const inp = inputOf(g.companyId);
      const label = groups.length > 1 ? `${companyName(g.companyId)}の` : '';
      if (!inp.date) {
        toast(`${label}精算日を入力してください`);
        return;
      }
      if (!inp.cashAccountId) {
        toast(`${label}精算に使う口座・現金を選んでください`);
        return;
      }
      if (methodOf(g.companyId) === 'bank_transfer' && inp.files.length === 0) {
        toast(`${label}振込明細を添付してください(振込のとき)`);
        return;
      }
      for (const f of inp.files) {
        const err = validateFile(f);
        if (err) {
          toast(err);
          return;
        }
      }
    }
    if (needsSignature && !signature) {
      toast('立替者に画面でサインしてもらってください(現金のとき)');
      return;
    }

    setBusy(true);
    let done = 0;
    /** 精算はできたが通知などの付随処理に失敗した会社の分(2026-09-17 改修依頼 B) */
    const warnings: string[] = [];
    try {
      const signatureImage = signature ? await loadImage(signature) : null;
      for (const g of groups) {
        const inp = inputOf(g.companyId);
        const account = accounts.find((a) => a.id === inp.cashAccountId)!;
        const method = methodOf(g.companyId);
        const company = companyName(g.companyId);
        try {
          const settlementId = newSettlementId();
          const pdf = await buildSettlementListPdf(
            {
              companyName: company,
              payeeName,
              settlementDate: inp.date,
              method,
              cashAccountName: accountLabel(account),
              issuerName: user.display_name,
              rows: g.items.map((t) => ({
                code: t.code ?? '',
                paymentDate: t.payment_date,
                account: t.account_id ? accountName(t.account_id) : '',
                description: t.description ?? '',
                amount: t.amount,
              })),
              signatureImage: method === 'cash' ? signatureImage : null,
            },
            `精算一覧_${company}_${payeeName}_${inp.date}.pdf`,
          );
          const [listPdf] = await uploadSettlementFiles(settlementId, 'list', [pdf], user.id);
          const evidence =
            inp.files.length > 0 ? await uploadSettlementFiles(settlementId, 'evidence', inp.files, user.id) : [];
          const result = await reimburseBatch({
            transactions: g.items,
            settlementId,
            settlementDate: inp.date,
            cashAccountId: inp.cashAccountId,
            method,
            listPdf: listPdf!,
            evidence,
            payeeId,
            payeeName,
            userId: user.id,
          });
          done += g.items.length;
          if (result.warning) warnings.push(`${company}: ${result.warning}`);
        } catch (e) {
          throw new Error(`${company}: ${e instanceof Error ? e.message : '精算に失敗しました'}`);
        }
      }
      const message = `${groups.length > 1 ? `${groups.length}社・` : ''}${done}件(${formatYen(total)}円)を精算しました。完了確認へ進みます`;
      reportFlowResult(toast, message, { warning: warnings.length > 0 ? warnings.join('\n') : null });
      onDone();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '精算に失敗しました');
      if (warnings.length > 0) window.alert(`途中まで精算した分の通知などに失敗しました。\n\n${warnings.join('\n')}`);
      // 途中の会社まで精算できている場合は一覧を更新し、残りをやり直せるように閉じる
      if (done > 0) {
        onDone();
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={() => (busy ? undefined : onClose())} width="min(820px, 94vw)" showClose>
      <h3 style={{ marginBottom: 2 }}>{transactions.length > 1 ? 'まとめて精算' : '立替精算(立替者へ支払う)'}</h3>
      <div className="path" style={{ margin: '2px 0 8px' }}>
        立替者: <b>{payeeName}</b> / {transactions.length}件 / 合計 <b>{formatYen(total)}円</b>
        {groups.length > 1 && <> / {groups.length}社</>}
        <br />
        会社ごとに精算日と精算に使う口座・現金を選びます。現金ならその場でサイン、銀行なら振込明細の添付です。
      </div>

      {groups.map((g) => {
        const inp = inputOf(g.companyId);
        const account = accountOf(g.companyId);
        const method = methodOf(g.companyId);
        const options = accountsFor(g.companyId);
        return (
          <div key={g.companyId} className="gray-box" style={{ background: '#fff', marginTop: 10 }}>
            <div className="box-title" style={{ fontSize: 13.5, color: 'var(--ink)' }}>
              {companyName(g.companyId)} — {g.items.length}件 / {formatYen(g.total)}円
            </div>

            {/* 精算する明細(一覧PDFと同じ内容) */}
            <div style={{ overflowX: 'clip', marginBottom: 10 }}>
              <table className="tx-table">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>管理番号</th>
                    <th style={{ width: 84 }}>支払日</th>
                    <th>科目</th>
                    <th>摘要</th>
                    <th className="num" style={{ width: 100 }}>金額</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((t) => (
                    <tr key={t.id}>
                      <td>{t.code}</td>
                      <td>{toShortDate(t.payment_date)}</td>
                      <td>{t.account_id ? accountName(t.account_id) : '—'}</td>
                      <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {t.description ?? ''}
                      </td>
                      <td className="num">{formatYen(t.amount)}円</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <div>
                <label>
                  精算日<span className="req">必須</span>
                </label>
                <DateField value={inp.date} onChange={(v) => patch(g.companyId, { date: v })} />
              </div>
              <div>
                <label>
                  精算に使う口座・現金<span className="req">必須</span>
                </label>
                <select value={inp.cashAccountId} onChange={(e) => patch(g.companyId, { cashAccountId: e.target.value })}>
                  <option value="">選択してください</option>
                  {options.list.map((a) => (
                    <option key={a.id} value={a.id}>
                      {options.fallback ? `${companyName(a.company_id ?? '')}: ` : ''}
                      {accountLabel(a)}
                    </option>
                  ))}
                </select>
                {options.fallback && (
                  <div className="hint" style={{ marginTop: 3 }}>
                    この会社の口座・現金が未登録のため全社の口座を表示しています(マスタ管理→支払い方法で登録できます)
                  </div>
                )}
              </div>
              {account && method === 'bank_transfer' && (
                <div className="fw">
                  <label>
                    振込明細<span className="req">必須</span>
                  </label>
                  <FileDropZone files={inp.files} onChange={(files) => patch(g.companyId, { files })} />
                </div>
              )}
              {account && method === 'cash' && (
                <div className="fw hint">現金で渡す分です。下の「受領のサイン」を立替者にもらってください。</div>
              )}
            </div>
          </div>
        );
      })}

      {needsSignature && (
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
            受領のサイン(現金で渡す分)<span className="req">必須</span>
          </label>
          {signature ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <img
                src={signature}
                alt="サイン"
                style={{ height: 60, border: '1px solid var(--line)', borderRadius: 6, background: '#fff' }}
              />
              <span className="badge b-ok">サイン済み</span>
              <button className="btn-line" onClick={() => setSigning(true)}>
                書き直す
              </button>
            </div>
          ) : (
            <div>
              <button className="btn-line" onClick={() => setSigning(true)}>
                立替者にサインしてもらう(タブレット・スマホ)
              </button>
              <p className="hint" style={{ marginTop: 6 }}>
                この画面を立替者に向けて、画面上でサインしてもらいます。サインは現金で渡す会社分の一覧PDFの受領欄に入ります。
              </p>
            </div>
          )}
        </div>
      )}

      <div className="modal-actions">
        <button className="btn-green" onClick={() => void submit()} disabled={busy}>
          {busy ? '処理中…' : transactions.length > 1 ? `${transactions.length}件をまとめて精算する` : '精算する'}
        </button>
        <button className="btn-line" onClick={onClose} disabled={busy}>
          キャンセル
        </button>
      </div>
      <p className="hint" style={{ marginTop: 10 }}>
        精算すると会社ごとに一覧PDFが自動で作られ、各申請の詳細と完了確認画面から開けます。精算後は承認者の完了確認に進みます。
      </p>

      {signing && (
        <SignaturePadModal
          title={`受領のサイン — ${payeeName} 様`}
          onClose={() => setSigning(false)}
          onDone={(dataUrl) => {
            setSignature(dataUrl);
            setSigning(false);
          }}
        />
      )}
    </Modal>
  );
}
